import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Layers, Users, ShieldCheck, Clock, Copy, Folder,
  Settings as SettingsIcon, Lock, Lock as LockOutline, LifeBuoy, LogOut, Home,
} from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup,
  CommandItem, CommandSeparator, CommandShortcut,
} from "../ui/command";
import { DialogTitle, DialogDescription } from "../ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useAuth } from "../../context/AuthContext";

/**
 * Global Cmd/Ctrl+K command palette. Mounted once in App.js and available on
 * every authenticated and public page. Auth-aware: hides app-only items when
 * signed out.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const signedIn = !!user;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const go = (to) => () => {
    setOpen(false);
    navigate(to);
  };

  const appItems = [
    { label: "Dashboard", to: "/app/dashboard", icon: LayoutDashboard, kw: "home overview metrics" },
    { label: "Onboarding Flows", to: "/app/flows", icon: Layers, kw: "flow builder new flow" },
    { label: "Active Clients", to: "/app/clients", icon: Users, kw: "client invitation send" },
    { label: "Compliance Checker", to: "/app/compliance", icon: ShieldCheck, kw: "scan compliance fca asa ico" },
    { label: "Audit Trail", to: "/app/audit-trail", icon: Clock, kw: "audit logs verify chain" },
    { label: "Templates", to: "/app/templates", icon: Copy, kw: "platform templates ifa mortgage" },
    { label: "Document Library", to: "/app/documents", icon: Folder, kw: "documents files" },
  ];

  const settingsItems = [
    { label: "Settings — Firm profile", to: "/app/settings#profile", icon: SettingsIcon },
    { label: "Settings — Branding", to: "/app/settings#branding", icon: SettingsIcon },
    { label: "Settings — Team", to: "/app/settings#team", icon: SettingsIcon },
    { label: "Settings — Security (2FA)", to: "/app/settings#security", icon: Lock },
    { label: "Settings — Billing", to: "/app/settings#billing", icon: SettingsIcon },
    { label: "Help & support", to: "/app/help", icon: LifeBuoy },
  ];

  const publicItems = [
    { label: "Public Security page", to: "/security", icon: LockOutline, kw: "trust security architecture gdpr" },
    { label: "Marketing home", to: "/", icon: Home, kw: "landing public" },
  ];

  const authItems = signedIn
    ? []
    : [
        { label: "Sign in", to: "/login", icon: Home },
        { label: "Start free trial", to: "/signup", icon: Home },
      ];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <VisuallyHidden>
        <DialogTitle>Command palette</DialogTitle>
        <DialogDescription>Quickly jump to any page in Compliche.</DialogDescription>
      </VisuallyHidden>
      <CommandInput placeholder="Type to search Compliche — Dashboard, Flows, Clients…" data-testid="command-palette-input" />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>No matching pages.</CommandEmpty>

        {signedIn && (
          <CommandGroup heading="Workspace">
            {appItems.map((it) => (
              <CommandItem key={it.to} value={`${it.label} ${it.kw || ""}`} onSelect={go(it.to)} data-testid={`cmdk-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <it.icon />
                <span>{it.label}</span>
                <CommandShortcut>↵</CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {signedIn && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Settings">
              {settingsItems.map((it) => (
                <CommandItem key={it.to} value={it.label} onSelect={go(it.to)} data-testid={`cmdk-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <it.icon />
                  <span>{it.label}</span>
                </CommandItem>
              ))}
              <CommandItem value="Sign out logout" onSelect={() => { setOpen(false); logout(); navigate("/"); }} data-testid="cmdk-sign-out">
                <LogOut />
                <span>Sign out</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <CommandGroup heading="Public">
          {publicItems.map((it) => (
            <CommandItem key={it.to} value={`${it.label} ${it.kw || ""}`} onSelect={go(it.to)} data-testid={`cmdk-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <it.icon />
              <span>{it.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {!signedIn && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Account">
              {authItems.map((it) => (
                <CommandItem key={it.to} value={it.label} onSelect={go(it.to)} data-testid={`cmdk-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <it.icon />
                  <span>{it.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
